import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isMatching, Pattern } from 'ts-pattern';

import type { UpdateProductPayload } from './types';

import { apiResourceSchema } from '../common/schemas';
import { fetchJson } from '../fetch';
import { QueryKeys } from '../keys';
import { productSchema } from './schemas';
import { productsQueryOptions } from './useProducts';

export const useUpdateProduct = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateProductPayload) => {
      const { data: product } = await fetchJson(`/products/${id}`, {
        method: 'PUT',
        schema: apiResourceSchema(productSchema),
        body,
      });

      return product;
    },
    onSuccess: (updatedProduct) => {
      queryClient.setQueryData(productsQueryOptions.queryKey, (products) =>
        (products ?? []).map((product) => (product.id === updatedProduct.id ? updatedProduct : product)),
      );

      queryClient.invalidateQueries({ queryKey: QueryKeys.products.index });
      queryClient.invalidateQueries({ queryKey: QueryKeys.quotations.root });
      queryClient.invalidateQueries({ queryKey: QueryKeys.invoices.root });
      queryClient.invalidateQueries({ queryKey: QueryKeys.invoiceTemplates.root });

      // It is important that the query cache is updated before the user navigates to the invoice/quotation/template form,
      // because the form lines might otherwise use stale data as default values.
      // (Form default values are only evaluated once on the initial render, so simply invalidating the queries wouldn't work.)
      //
      // Hence, we refetch the queries here and await the result. Note that we don't know which documents use the updated product,
      // so we have to refetch them all.

      return queryClient.refetchQueries({
        predicate: ({ queryKey }) =>
          isMatching([...QueryKeys.invoices.show.root, Pattern.number], queryKey) ||
          isMatching([...QueryKeys.quotations.show.root, Pattern.number], queryKey) ||
          isMatching([...QueryKeys.invoiceTemplates.show.root, Pattern.number], queryKey),
      });
    },
  });
};
